import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { getTitleComment } from 'lib/utils';
import {
  QuestionType,
  ReportQuestion as ReportQuestionType,
} from '../../../types';
import { Button } from 'components/ui/button';
import useAuthStore from 'store/auth';

interface ReportQuestionProps {
  question: ReportQuestionType | undefined;
  userInput: string;
  selectedChoices: string[];
  otherValue: string;
  setUserInput: Dispatch<SetStateAction<string>>;
  setOtherValue: Dispatch<SetStateAction<string>>;
  onSelect: (checked: boolean, eng: string, es: string) => void;
  onSubmittedSelect: (eng: string, es: string) => void;
}

export interface DropdownOption {
  label: string;
  choices: string | string[];
  choice: string;
  spanishChoice: string;
}

export default function ReportQuestion({
  question,
  userInput,
  otherValue,
  selectedChoices,
  onSelect,
  setUserInput,
  setOtherValue,
  onSubmittedSelect,
}: ReportQuestionProps) {
  const { t } = useTranslation();
  const locale = useAuthStore((store) => store.locale);

  const langQuestion =
    locale === 'es' ? question?.spanish_question : question?.eng_question;
  const { title, comment } = getTitleComment(langQuestion || '');

  const langChoices =
    locale === 'es' ? question?.spanish_choices : question?.eng_choices;
  const options = useMemo(
    () =>
      question?.question_type === QuestionType.MULTI_SELECT
        ? JSON.parse(langChoices || '[]')
            ?.map((option: string, index: number) => {
              const { title: choice, comment } = getTitleComment(option);

              const { title: spanishChoice } = getTitleComment(
                JSON.parse(question.spanish_choices)[index]
              );

              return {
                label: `${choice} ${
                  comment
                    ? comment.startsWith('(')
                      ? comment
                      : `- ${comment}`
                    : ''
                }`,
                choice,
                spanishChoice,
              };
            })
            .filter((option: { choice: string }) => option?.choice)
        : [],
    [question]
  );

  if (!question) return null;

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='mb-2 text-lg font-medium leading-tight'>{title}</h2>
        <p className='text-sm leading-tight'>{comment}</p>
      </div>

      {question.question_type === QuestionType.SUBMITTED ||
      ['config', 'no_scan_data'].includes(question.path_type) ? (
        <div className='flex flex-col sm:flex-row items-center justify-center gap-4 !mt-8'>
          {JSON.parse(langChoices || '[]')?.map(
            (choice: string, index: number) => {
              if (question.path_type === 'no_scan_data' && choice === 'Exit')
                return null;

              return (
                <Button
                  key={choice}
                  variant='outline'
                  className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
                  onClick={() => {
                    const engChoices = JSON.parse(question.eng_choices);
                    const spanishChoices = JSON.parse(question.spanish_choices);
                    onSubmittedSelect(engChoices[index], spanishChoices[index]);
                  }}
                >
                  {choice}
                </Button>
              );
            }
          )}
        </div>
      ) : null}

      {question.question_type === QuestionType.TEXT ? (
        <Textarea
          className='font-medium text-blue-500 border-blue-300 h-28 placeholder:text-blue-500/60 focus-visible:ring-transparent focus-visible:ring-0'
          placeholder={t('write_here')}
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
        />
      ) : null}

      {question.question_type === QuestionType.MULTI_SELECT ? (
        <div className='grid grid-cols-1 gap-2 mt-3 lg:grid-cols-2 gap-x-8'>
          {options.map(
            ({ label, choice, spanishChoice }: DropdownOption, ind: number) => {
              const isSelected = selectedChoices.includes(choice);
              return (
                <div key={`${label}-${ind}`}>
                  <div className='flex items-center gap-x-2'>
                    <Checkbox
                      className='data-[state=checked]:bg-primary data-[state=checked]:text-white border-primary'
                      checked={isSelected}
                      onCheckedChange={(checked: boolean) =>
                        onSelect(checked, choice, spanishChoice)
                      }
                      id={label}
                    />
                    <label className='text-sm leading-tight' htmlFor={label}>
                      {label}
                    </label>
                  </div>
                  {isSelected && choice === 'Other' ? (
                    <div className='ml-6'>
                      <Input
                        className='h-7'
                        value={otherValue}
                        onChange={(e) => setOtherValue(e.target.value)}
                        data-testid='other-value'
                      />
                    </div>
                  ) : null}
                </div>
              );
            }
          )}
        </div>
      ) : null}
    </div>
  );
}
